import { BookOpen, Info, ExternalLink } from "lucide-react";

const stats = [
  { val: "50+", label: "Estados con SR-22" },
  { val: "3 años", label: "Duración típica" },
  { val: "$25", label: "Tasa de presentación" },
];

const infoLinks = [
  { label: "DMV California — SR-22", href: "https://www.dmv.ca.gov/portal/driver-education-and-safety/educational-materials/fast-facts/financial-responsibility-insurance-requirements-for-vehicle-registration-ffvr-18/", desc: "Información oficial del estado" },
  { label: "DMV Texas — SR-22", href: "https://www.dps.texas.gov/section/driver-license/financial-responsibility-insurance-certificate-sr-22", desc: "Requisitos de responsabilidad financiera" },
  { label: "DMV Florida — FR-44", href: "https://www.flhsmv.gov/driver-licenses-id-cards/education-courses/dui-and-iid/dui-faqs/", desc: "Información oficial Florida" },
];

function FadeUp({ children, delay = 0, className = "", priority = false }: { children: React.ReactNode; delay?: number; className?: string; priority?: boolean }) {
  if (priority) {
    return <div className={className}>{children}</div>;
  }
  return (
    <div
      className={`animate-fade-up ${className}`}
      style={{ animationDelay: `${delay}s`, animationFillMode: "both" }}
    >
      {children}
    </div>
  );
}

export default function HeroSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "hsl(var(--secondary))" }}
    >
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, hsl(var(--primary)) 0%, transparent 55%),
                           radial-gradient(circle at 80% 50%, hsl(var(--accent)) 0%, transparent 55%)`,
        }}
      />

      <div className="relative container mx-auto px-4 sm:px-6 py-8 md:py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center max-w-5xl mx-auto">

          {/* Mobile-only: image at top */}
          <div className="lg:hidden flex justify-center">
            <div
              className="relative rounded-2xl overflow-hidden w-full max-w-[320px]"
              style={{
                background: "hsl(var(--primary) / 0.2)",
                border: "1px solid hsl(var(--primary-light) / 0.3)",
              }}
            >
              <img
                src="/images/sr22-hero-person.webp"
                alt="Estiven Gabriel Aguiluz Suazo — Fundador de SeguroSR22USA"
                className="w-full h-auto object-cover object-top"
                width={320}
                height={280}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                style={{ maxHeight: "280px" }}
              />
            </div>
          </div>

          {/* Left: Text */}
          <div>
            <FadeUp delay={0}>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 border"
                style={{
                  background: "hsl(var(--accent) / 0.15)",
                  borderColor: "hsl(var(--accent) / 0.35)",
                  color: "hsl(var(--accent))",
                }}
              >
                <Info className="w-3 h-3" />
                Guía informativa gratuita · Sin registro requerido
              </div>
            </FadeUp>

            <FadeUp delay={0.1} priority>
              <h1
                className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4"
                style={{ color: "hsl(0 0% 100%)" }}
              >
                Todo lo que necesitas saber sobre el{" "}
                <span style={{ color: "hsl(var(--accent))" }}>
                  Seguro SR-22
                </span>{" "}
                en EE.UU.
              </h1>
            </FadeUp>

            <FadeUp delay={0.2}>
              <p
                className="text-base md:text-lg leading-relaxed mb-8"
                style={{ color: "hsl(215 20% 65%)" }}
              >
                Información clara y gratuita sobre el certificado SR-22:
                quién lo necesita, cuánto cuesta, dónde obtenerlo y cómo
                funciona. <strong style={{ color: "hsl(0 0% 90%)" }}>En español.</strong>
              </p>
            </FadeUp>

            <FadeUp delay={0.25}>
              <div
                className="rounded-xl p-4 mb-5"
                style={{
                  background: "hsl(0 0% 100% / 0.06)",
                  border: "1px solid hsl(0 0% 100% / 0.12)",
                }}
              >
                <p className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: "hsl(var(--accent))" }}>
                  ¿Qué hacemos?
                </p>
                <p className="text-sm leading-relaxed" style={{ color: "hsl(215 20% 75%)" }}>
                  Te explicamos el SR-22 en español y te conectamos con opciones para cotizar según tu estado.{" "}
                  <strong style={{ color: "hsl(0 0% 90%)" }}>No somos aseguradora;</strong> nuestra misión es
                  ayudarte a cumplir requisitos sin perder tiempo ni dinero.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <a href="#guia" className="btn-cta px-6 py-3 text-sm text-center">
                  <BookOpen className="w-4 h-4" />
                  Leer la Guía SR-22 →
                </a>
                <a
                  href="#aseguradoras"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm border-2 transition-all duration-200 hover:bg-white/10"
                  style={{
                    color: "hsl(0 0% 100%)",
                    borderColor: "hsl(0 0% 100% / 0.3)",
                  }}
                >
                  Ver aseguradoras
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.35}>
              <div
                className="rounded-xl p-4 mb-6"
                style={{
                  background: "hsl(0 0% 100% / 0.05)",
                  border: "1px solid hsl(0 0% 100% / 0.1)",
                }}
              >
                <p className="text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: "hsl(215 20% 55%)" }}>
                  Recursos oficiales del DMV
                </p>
                <div className="flex flex-col gap-1.5">
                  {infoLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs group"
                      style={{ color: "hsl(215 20% 70%)" }}
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: "hsl(var(--accent))" }} />
                      <span className="group-hover:underline">{link.label}</span>
                      <span style={{ color: "hsl(215 20% 45%)" }}>— {link.desc}</span>
                    </a>
                  ))}
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.4}>
              <div
                className="grid grid-cols-3 gap-4 pt-5 border-t"
                style={{ borderColor: "hsl(0 0% 100% / 0.1)" }}
              >
                {stats.map(({ val, label }) => (
                  <div key={label} className="text-center">
                    <div
                      className="text-2xl md:text-3xl font-extrabold mb-0.5"
                      style={{ color: "hsl(var(--accent))" }}
                    >
                      {val}
                    </div>
                    <div className="text-xs" style={{ color: "hsl(215 20% 60%)" }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>

          {/* Right: Image — desktop only */}
          <div className="hidden lg:flex relative justify-end">
            <div
              className="relative rounded-2xl overflow-hidden shadow-hero max-w-sm w-full"
              style={{
                background: "hsl(var(--primary) / 0.2)",
                border: "1px solid hsl(var(--primary-light) / 0.3)",
              }}
            >
              <img
                src="/images/sr22-hero-person.webp"
                alt="Estiven Gabriel Aguiluz Suazo — Fundador de SeguroSR22USA"
                className="w-full h-auto object-cover object-top"
                width={480}
                height={600}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
              />
              <div
                className="absolute bottom-4 left-4 right-4 rounded-xl p-3 flex items-center gap-3"
                style={{
                  background: "hsl(var(--secondary) / 0.92)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid hsl(var(--accent) / 0.2)",
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs font-bold" style={{ color: "hsl(0 0% 100%)" }}>
                    Información gratuita y sin compromiso
                  </div>
                  <div className="text-xs" style={{ color: "hsl(215 20% 65%)" }}>
                    Guías actualizadas 2026 · En español
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
